import React from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import toast from "react-hot-toast";
import { Copy } from "lucide-react";

import StatusBadge from "../../../components/StatusBadge";
import { Separator } from "../../../components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../components/ui/sheet";
import { formatLongDate } from "../../../utils/formatters";
import type { ActivityRow } from "./normalizeActivity";

type Props = {
  activity: ActivityRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const Row = ({
  label,
  value,
  copyValue,
}: {
  label: string;
  value: React.ReactNode;
  copyValue?: string;
}) => (
  <div className="flex items-start justify-between gap-4 py-3">
    <span className="shrink-0 text-[13px] font-medium text-muted-foreground">
      {label}
    </span>
    <span className="flex min-w-0 items-center gap-2 text-right text-[13px] font-semibold text-foreground">
      <span className="truncate">{value}</span>
      {copyValue && (
        <CopyToClipboard
          text={copyValue}
          onCopy={() => toast.success(`${label} copied`)}
        >
          <button type="button" aria-label={`Copy ${label}`} className="shrink-0">
            <Copy className="size-3.5 text-muted-foreground transition-colors hover:text-primary" />
          </button>
        </CopyToClipboard>
      )}
    </span>
  </div>
);

const ActivityDetailSheet = ({ activity, open, onOpenChange }: Props) => {
  if (!activity) return null;

  const raw = activity.raw ?? {};
  const isPayout = activity.type === "Payout";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto font-manrope mds:max-w-[420px]">
        <SheetHeader className="pr-10 text-left">
          <SheetTitle className="font-sora text-[20px]">
            {activity.amountLabel}
          </SheetTitle>
          <SheetDescription>
            {activity.type} • {activity.nairaLabel}
          </SheetDescription>
          <StatusBadge status={activity.status} className="mt-2 w-fit" />
        </SheetHeader>

        <Separator className="my-4" />

        <div className="divide-y">
          <Row label="Date" value={formatLongDate(activity.date)} />
          <Row label="Type" value={activity.type} />
          <Row label="Amount" value={activity.amountLabel} />
          {activity.usdLabel && (
            <Row label="Amount in USD" value={activity.usdLabel} />
          )}
          <Row label="Naira value" value={activity.nairaLabel} />

          {isPayout && raw?.recipient && (
            <>
              <Row label="Account name" value={raw.recipient.accountName ?? "—"} />
              <Row
                label="Account number"
                value={raw.recipient.accountNumber ?? "—"}
                copyValue={raw.recipient.accountNumber}
              />
              <Row label="Bank" value={raw.recipient.bankName ?? "—"} />
            </>
          )}

          {raw?.network && (
            <Row label="Network" value={String(raw.network).toUpperCase()} />
          )}
          {raw?.toAddress && (
            <Row
              label="To address"
              value={raw.toAddress}
              copyValue={raw.toAddress}
            />
          )}

          <Row
            label="Transaction ID"
            value={raw?.txid ?? raw?._id ?? "—"}
            copyValue={raw?.txid ?? raw?._id}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ActivityDetailSheet;
